/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   useCarouselV3.tsx
 */
import React, { useRef, useCallback, useState } from 'react'
import Carousel, { CarouselInternalState, SkipCallbackOptions } from 'react-multi-carousel'

import { CarouselV3Base } from './CarouselV3Base'
import { nextSlide, previousSlide } from './CarouselV3Controls/helpers'
import { CarouselV3Props } from './types'

const notMountedError = () => {
  throw new Error('Carousel not mounted, ref doesnt not exist')
}

// Hook version of carousel, to be able to render the carousel and call carousel functions separately
/**
 * @deprecated use CarouselV4 from @zcode-ui/containers instead
 */
export const useCarouselV3 = () => {
  const [carouselMounted, setCarouselMounted] = useState(false)
  const [carouselState, setCarouselState] = useState<CarouselInternalState>({
    // Set defaults so everything doesnt have to be optional
    itemWidth: 0,
    containerWidth: 0,
    slidesToShow: 1,
    currentSlide: 0,
    totalItems: 0,
    domLoaded: false,
    deviceType: '',
    transform: 0,
  })

  const carouselRef = useRef<Carousel | null>(null)
  const carouselStateRef = useRef<CarouselInternalState>(carouselState)
  const carouselTouched = useRef(false)

  const setRef = useCallback(
    (ref: Carousel | null, initialIndex: number | undefined, hasSlided: boolean) => {
      if (!ref) {
        return
      }
      // We dont want the carousel obj in state that would be very unperformant
      carouselRef.current = ref
      let { state } = ref

      if (!carouselMounted) {
        // See README.md for details, skips callback since carouselState is already set here and we dont want to preload images on init
        if (initialIndex) {
          ref.goToSlide(initialIndex, true)
          state = { ...state, currentSlide: initialIndex }
        }
        setCarouselMounted(true)
      } else if (
        initialIndex &&
        !carouselTouched.current &&
        carouselStateRef.current.currentSlide === initialIndex &&
        state.currentSlide === 0 &&
        !hasSlided
      ) {
        ref.goToSlide(initialIndex, true)
      } else if (!carouselTouched.current && state.currentSlide !== (initialIndex ?? 0)) {
        carouselTouched.current = true
      }
      carouselStateRef.current = state
      setCarouselState(state)
    },
    [carouselMounted]
  )

  // This prevents react from updating the carousel after init, this is because
  // when preloading images or certain other updates related to the children
  // the carousel would rerender entirely and reset to index 0
  // setting the carousel state into react state also reloads the carousel
  const MemoizedCarousel = useCallback(
    (props: CarouselV3Props) => (
      <CarouselV3Base setCarouselState={setCarouselState} setRef={setRef} {...props} />
    ),
    [setRef]
  )

  // Next three useCallbacks see README.md for details
  const handlePreviousSlide = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!carouselMounted || !carouselRef?.current?.goToSlide || !carouselRef?.current?.previous) {
        return notMountedError()
      }

      previousSlide(
        carouselState.currentSlide,
        carouselState.totalItems,
        carouselRef.current.goToSlide,
        carouselRef.current.previous
      )(e)
    },
    [carouselMounted, carouselState]
  )

  const handleNextSlide = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!carouselMounted || !carouselRef?.current?.goToSlide || !carouselRef?.current?.next) {
        return notMountedError()
      }

      nextSlide(
        carouselState.currentSlide,
        carouselState.totalItems,
        carouselRef.current.goToSlide,
        carouselRef.current.next
      )(e)
    },
    [carouselMounted, carouselState]
  )

  const handleGoToSlide = useCallback(
    (index: number, skipCallbacks?: SkipCallbackOptions | undefined) => {
      if (Number.isNaN(index) || index === undefined || index === null) {
        console.error('CarouselV3 - handleGoToSlide() index is NaN, null, or undefined')
        return
      }
      if (!carouselMounted) {
        return notMountedError()
      }
      if (!carouselRef?.current?.goToSlide) {
        return notMountedError()
      }

      let calcedIndex = index
      const finalIndex = carouselRef.current.state.totalItems - 1

      // If someone tries to set out of bounds index, just loop back around
      if (finalIndex < index) {
        calcedIndex = 0
        console.error('CarouselV3 - error attempting to go to out of bounds index, new index = 0')
      } else if (index < 0) {
        calcedIndex = finalIndex
        console.error(
          'CarouselV3 - error attempting to go to out of bounds index, new index = last index'
        )
      }

      carouselRef.current.goToSlide(calcedIndex, skipCallbacks)
    },
    [carouselMounted]
  )

  return {
    carouselMounted,
    previousSlide: handlePreviousSlide,
    nextSlide: handleNextSlide,
    goToSlide: handleGoToSlide,
    ...carouselState,
    CarouselV3: MemoizedCarousel,
  }
}
